import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { concat, Subject, Subscription } from 'rxjs';
import { map, switchMap, tap, concatMap, debounceTime, distinctUntilChanged, filter, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { DocListEntry, DocTypes } from 'src/app/shared/doc-list-entry';
import { DocumentListViewType } from 'src/app/shared/document-list-view-type';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * Modal dialog for searching documents by multiple text filters.
 *
 * **Pipeline:** `listOptions$` → debounced 200 ms + `distinctUntilChanged` →
 * `filter` (each search string must be empty OR at least 3 chars) →
 * `switchMap(getDocListDateEx)` → `datasource` limited to 100 results.
 * A user warning is shown when any search string exceeds 2 chars, as the
 * underlying SQL query can take up to 45 seconds.
 *
 * `parentCompany` is resolved from global settings on init and passed as the
 * `parent` parameter to `getDocListDateEx`.
 *
 * On confirm, `selectedDocument` emits the `DOCID` of the selected row.
 * `displayChange` emits `false` on hide for two-way `[(display)]` binding.
 */
@Component({
  selector: 'app-documents-search',
  templateUrl: './documents-search.component.html',
  styleUrls: ['./documents-search.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentsSearchComponent implements OnInit, OnChanges, OnDestroy {

  private topDocQuery: Subscription;
  private selectedDocQuery: Subscription;

  parentCompany: number;

  displayDS: boolean = false;

  /** Dialog header text. */
  @Input() header: string = "Поиск документов";

  /** Document number / reference search string (passed as `sStr1`). */
  @Input() docStrString: string = '';
  /** Client name search string (passed as `sStr2`). */
  @Input() cliStrString: string = '';
  /** Location / LCO name search string (passed as `sStr3`). */
  @Input() lcoStrString: string = '';
  /** Product name search string (passed as `sStr4`). */
  @Input() proStrString: string = '';

  modal: boolean = true;
  loading: boolean;

  rows: number = 10;
  totalRecords: number;

  selection: DocListEntry;

  datasource: DocListEntry[] = [];

  /** Controls dialog visibility; supports two-way binding via `displayChange`. */
  @Input()
  display: boolean = false;

  /** Emits the `DOCID` of the confirmed row selection. */
  @Output()
  public selectedDocument: EventEmitter<number> = new EventEmitter();

  /** Emits `false` on dialog hide to support two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  private listOptions$ = new Subject<{ parent: number, view: number, fromdate: Date, search: string, sStr1: string, sStr2: string, sStr3: string, sStr4: string }>();

  constructor(
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private router: Router,
  ) { }

  /** Unsubscribes `topDocQuery` and `selectedDocQuery` subscriptions. */
  ngOnDestroy(): void {
    console.log('*** DocumentsSearchComponent ngOnDestroy: ');

    if (this.topDocQuery) { this.topDocQuery.unsubscribe(); }
    if (this.selectedDocQuery) { this.selectedDocQuery.unsubscribe(); }
  }

  /** Resolves `parentCompany` from global settings and wires up the `listOptions$` search pipeline. */
  ngOnInit(): void {
    console.log('*** DocumentsSearchComponent ngOnInit: ');

    this.parentCompany = this.globalsettingsservice.getMainCompanyId();

    this.topDocQuery =
      this.listOptions$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        filter((o: any) => {
          return (
            ((o.sStr1.length == 0) || (o.sStr1.length > 2)) &&
            ((o.sStr2.length == 0) || (o.sStr2.length > 2)) &&
            ((o.sStr3.length == 0) || (o.sStr3.length > 2)) &&
            ((o.sStr4.length == 0) || (o.sStr4.length > 2))
          );
        }),

        tap((opt: any) => {
          this.loading = true;
          if ((opt.sStr1.length > 2) || (opt.sStr2.length > 2) || (opt.sStr3.length > 2) || (opt.sStr4.length > 2)) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Поиск по параметрам может занимать до 45 секунд!!!',
              detail: 'В случае возникновения ошибки обновите страницу'
            });
          }
        }),

        switchMap((opt: any) => this.docApi.getDocListDateEx(opt.parent, opt.view, opt.fromdate, opt.sStr1, opt.sStr2, opt.sStr3, opt.sStr4).pipe(

          map(data => {
            this.datasource = data.slice(0, 100);
            this.totalRecords = this.datasource.length;
          }),

          finalize(() => this.loading = false))
        )

    ).subscribe({
      error: (err) => {
        this.loading = false;
        console.error('Error occurred while fetching document list:', err);
      }
    });
  }

  /** Reacts to `display` input changes — body is currently a no-op. */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];
      console.log('*** DocumentsSearchComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
//      console.log('*** DocumentsSearchComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'display':
          break;
      }
    }
  }

  /** No-op — called by PrimeNG dialog `(onShow)`. */
  onShowDialog() {
    console.log('*** DocumentsSearchComponent onShow: ');
  }

  /** Emits `false` via `displayChange` to close the dialog. */
  onHideDialog() {
    console.log('*** DocumentsSearchComponent onHide');
    this.displayChange.emit(false);
  }

  /** Pushes all four search strings as a `listOptions$` payload to trigger the search pipeline. */
  onSearch(event) {
    console.log('*** DocumentsSearchComponent onSearch');

    this.listOptions$.next({
      parent: this.parentCompany,
      view: DocumentListViewType.ALL,
      fromdate: new Date(1900, 1, 1),
      search: this.docStrString,
      sStr1: this.docStrString.trim(),
      sStr2: this.cliStrString.trim(),
      sStr3: this.lcoStrString.trim(),
      sStr4: this.proStrString.trim()
    });

  }

  /** No-op row-select hook (selection is tracked by PrimeNG binding on `selection`). */
  onRowSelect(event) {
    console.log('*** DocumentsSearchComponent onRowSelect');
  }

  /** Emits `selection.DOCID` via `selectedDocument` and closes the dialog. */
  onConfirm(text: string) {
    console.log('*** DocumentsSearchComponent onConfirm: ' + text);
    if (this.selection) {
      this.selectedDocument.emit(this.selection.DOCID);
      this.display = false;
    }
  }

}
